import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import AppShell from '@/components/feature/AppShell';
import MetricCard from '@/pages/dashboard/admin/components/MetricCard';
import ActivityFeed from '@/pages/dashboard/admin/components/ActivityFeed';
import WeeklyAttendanceChart from '@/pages/dashboard/admin/components/WeeklyAttendanceChart';
import CheckInWidget from '@/components/feature/CheckInWidget';
import CreateAnnouncementModal from '@/pages/dashboard/admin/components/CreateAnnouncementModal';
import AnnouncementsSection from '@/pages/dashboard/admin/components/AnnouncementsSection';
import { useToast } from '@/components/base/Toast';
import { useCheckIn } from '@/hooks/useCheckIn';
import { adminMetrics, notifications } from '@/mocks/dashboard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { incidentLogs } from '@/mocks/preInspection';
import { teacherLessonPlans } from '@/mocks/lessonPlanList';
import { announcements as initialAnnouncements, type Announcement } from '@/mocks/announcements';

const metricLabelKeys: Record<string, string> = {
  students: 'totalStudents',
  therapists: 'activeTherapists',
  sessions: 'sessionsThisWeek',
  pending: 'pendingFollowUps',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { isCheckedOut } = useCheckIn(currentUser.name);
  const toast = useToast();
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12 ? t('common.goodMorning') : hour < 17 ? t('common.goodAfternoon') : t('common.goodEvening');

  // Announcements state
  const [announcementsList, setAnnouncementsList] = useState<Announcement[]>([...initialAnnouncements]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateAnnouncement = (data: {
    message: string;
    audioBlob?: Blob;
    facultyIds: string[] | 'all';
    parentIds: string[] | 'all';
    scheduleAt?: string;
  }) => {
    const newAnnouncement: Announcement = {
      id: `ann-${Date.now()}`,
      message: data.message,
      audioUrl: data.audioBlob ? URL.createObjectURL(data.audioBlob) : undefined,
      sentToFaculty: data.facultyIds,
      sentToParents: data.parentIds,
      sentBy: { name: currentUser.name, role: currentUser.role, avatar: currentUser.initials },
      createdAt: new Date().toISOString(),
      publishedAt: new Date().toISOString(),
      scheduledFor: data.scheduleAt,
      status: data.scheduleAt ? 'scheduled' : 'published',
      readBy: [currentUser.name],
    };

    setAnnouncementsList((prev) => [newAnnouncement, ...prev]);
    setShowCreateModal(false);
    toast.success(
      data.scheduleAt ? t('dashboard.announcementScheduled') : t('dashboard.announcementCreated')
    );
  };

  const handleDeleteAnnouncement = (id: string) => {
    setAnnouncementsList((prev) => prev.filter((a) => a.id !== id));
    toast.success(t('dashboard.announcementDeleted'));
  };

  // Compute unresolved incident counts
  const unresolvedIncidents = incidentLogs.filter((inc) => inc.followUpRequired);
  const unresolvedCount = unresolvedIncidents.length;
  const totalIncidents = incidentLogs.length;

  // Compute org-wide lesson plan stats across all teachers
  const nonTemplatePlans = teacherLessonPlans.filter((p) => !p.isTemplate);
  const totalLessonPlans = nonTemplatePlans.length;
  const submittedCount = nonTemplatePlans.filter((p) => p.status === 'submitted').length;
  const inProgressCount = nonTemplatePlans.filter((p) => p.status === 'in_progress').length;
  const completedCount = nonTemplatePlans.filter((p) => p.status === 'completed').length;
  const draftCount = nonTemplatePlans.filter((p) => p.status === 'draft').length;
  const lessonPlanChangeText = `${submittedCount} submitted, ${inProgressCount} in progress, ${completedCount} completed, ${draftCount} drafts`;

  return (
    <AppShell
      pageTitle={t('dashboard.adminDashboard')}
      pageSubtitle={t('dashboard.adminSubtitle')}
      breadcrumbs={[{ label: t('common.home') }, { label: t('common.dashboard') }]}
      notifications={notifications}
    >
      {/* Greeting Banner */}
      <div className="gradient-hero rounded-2xl p-4 sm:p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.72 0.17 58), transparent 70%)', transform: 'translate(30%, -30%)' }}
        />
        <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, oklch(0.60 0.12 215), transparent 70%)', transform: 'translateY(40%)' }}
        />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-foreground-300 text-xs sm:text-sm font-label font-medium mb-1">
              {now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <h2 className="font-heading font-700 text-foreground-50 text-xl sm:text-2xl">
              {greeting}, {currentUser.name.split(' ')[0]}! 👋
            </h2>
          </div>
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-foreground-50/10 border border-foreground-50/20 text-foreground-100 text-sm font-label font-medium rounded-lg hover:bg-foreground-50/20 transition-colors cursor-pointer whitespace-nowrap flex-1 sm:flex-none">
              <i className="ri-file-chart-2-line" />
              {t('dashboard.reports')}
            </button>
          </div>
        </div>
      </div>

      {/* Check-In / Check-Out Widget */}
      <CheckInWidget staffName={currentUser.name} />

      {/* Unresolved Incident Alert Banner */}
      {unresolvedCount > 0 && (
        <div className="mb-5 p-4 bg-red-50 rounded-xl border border-red-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
              <i className="ri-alert-fill text-lg text-red-600" />
            </div>
            <div>
              <p className="text-sm font-heading font-700 text-red-800">
                {t('dashboard.unresolvedIncidentsTitle', { count: unresolvedCount })}
              </p>
              <p className="text-xs text-red-600 font-label mt-0.5">
                {t('dashboard.unresolvedIncidentsDesc', { total: totalIncidents, unresolved: unresolvedCount })}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard/admin/students')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-label font-medium rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-arrow-right-line" />
            {t('dashboard.reviewIncidents')}
          </button>
        </div>
      )}

      {/* Dashboard content */}
      <div>
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {adminMetrics.map((metric) => (
            <MetricCard
              key={metric.id}
              label={t(`dashboard.${metricLabelKeys[metric.id] || metric.label}`, { defaultValue: metric.label })}
              value={metric.value}
              change={metric.change}
              trend={metric.trend as 'up' | 'down' | 'warning' | 'neutral'}
              icon={metric.icon}
              iconBg={metric.iconBg}
              iconColor={metric.iconColor}
              accent={metric.accent}
              onClick={
                metric.id === 'students'
                  ? () => navigate('/dashboard/admin/students')
                  : metric.id === 'therapists'
                    ? () => navigate('/dashboard/admin/teachers')
                    : metric.id === 'sessions'
                      ? () => navigate('/dashboard/admin/attendance')
                      : metric.id === 'pending'
                        ? () => navigate('/dashboard/admin/staff-attendance')
                        : undefined
              }
            />
          ))}

          {/* Lesson Plans Metric Card — org-wide stats across all teachers */}
          <MetricCard
            label={t('dashboard.orgLessonPlans')}
            value={String(totalLessonPlans)}
            change={lessonPlanChangeText}
            trend="up"
            icon="ri-file-list-3-line"
            iconBg="bg-accent-100"
            iconColor="text-accent-700"
            accent="border-l-4 border-l-accent-500"
          />

          {/* Incidents Metric Card — always visible, doubles as notification badge */}
          <MetricCard
            label={t('dashboard.incidentsTracked')}
            value={String(totalIncidents)}
            change={unresolvedCount > 0 ? t('dashboard.unresolvedCount', { count: unresolvedCount }) : t('dashboard.allResolved')}
            trend={unresolvedCount > 0 ? 'warning' : 'up'}
            icon="ri-alert-line"
            iconBg="bg-red-50"
            iconColor="text-red-600"
            accent="border-l-4 border-l-red-500"
            onClick={() => navigate('/dashboard/admin/students')}
          />
        </div>

        {/* Activity Feed — full width */}
        <div className="mb-6">
          <ActivityFeed />
        </div>

        {/* Weekly Attendance Chart */}
        <div className="mb-6">
          <WeeklyAttendanceChart />
        </div>

        {/* Announcements Section */}
        <div className="mt-6">
          <AnnouncementsSection
            announcements={announcementsList}
            currentUser={currentUser.name}
            onCreateClick={() => setShowCreateModal(true)}
            onDelete={handleDeleteAnnouncement}
          />
        </div>
      </div>

      {/* Create Announcement Modal */}
      <CreateAnnouncementModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onPublish={handleCreateAnnouncement}
      />
    </AppShell>
  );
}